import { FieldInput } from "./FieldInput";
import { useEffect, useState } from "react";
import { View } from "react-native";
import { Calendar } from "lucide-react-native";
import {
  formatDateTyping,
  isoToDisplay,
  parseTypedToIso,
  placeholderFor,
  useDateFormat,
} from "@src/packages/utils/useDateFormat";
import { colors } from "../theme/tokens";
import { MoonFieldInput } from "./MoonFieldInput";

/**
 * Web typeable date input, at every viewport width.
 *
 * Reverted from `react-native-advanced-input-mask` (commit 49c95b94e)
 * because the masked variant used `defaultValue` (uncontrolled) and only
 * emitted `onChange` when its internal `complete` flag fired AND
 * `parseTypedToIso` returned a non-empty string. If either failed —
 * mask quirk, format mismatch, partial input — the parent state stayed
 * empty even though the user typed a full date, leaving downstream
 * "Next" / "Review" buttons greyed (UAT issue #103 follow-up: the
 * AddAppointment Review button wouldn't enable after typing a date).
 *
 * Controlled typing uses `value={display}` and `formatDateTyping`. Only a
 * complete valid ISO date or an explicit clear reaches the parent; partial
 * typing stays in the editor until it resolves to a valid day. Leaving the
 * field with text that is not a whole, real date reports it through
 * `onIncompleteChange`, so the field can say so instead of keeping the old or
 * empty value without a word.
 */
export function DateTextInput({
  value,
  onChange,
  placeholder,
  hasError,
  showLeadingIcon = true,
  disabled,
  appearance = "default",
  accessibilityLabel,
  autoFocus,
  onValidityChange,
  onIncompleteChange,
}: {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  hasError: boolean;
  showLeadingIcon?: boolean;
  disabled?: boolean;
  appearance?: "default" | "glass";
  accessibilityLabel: string;
  autoFocus?: boolean;
  onValidityChange?: (valid: boolean) => void;
  /** True once she leaves half a date; false again once it is whole or clear. */
  onIncompleteChange?: (incomplete: boolean) => void;
}) {
  const { dateFormat } = useDateFormat();
  const [display, setDisplay] = useState(() => isoToDisplay(value, dateFormat));

  // Keep the displayed text in sync when the parent's ISO value changes
  // (e.g. on edit-mode mount or an external reset). The dependency on
  // `value` is exactly the right signal — within a single typing
  // session `value` only changes after we ourselves emit a valid ISO,
  // so this won't fight the user mid-keystroke.
  useEffect(() => {
    setDisplay((prev) => {
      const next = isoToDisplay(value, dateFormat);
      return prev === next ? prev : next;
    });
    onIncompleteChange?.(false);
  }, [value, dateFormat, onIncompleteChange]);

  return (
    <MoonFieldInput
      hasError={hasError}
      appearance={appearance}
      paddingY={appearance === "glass" ? 0 : 2}
      style={{ justifyContent: "center" }}
    >
      <View
        style={{
          flex: 1,
          flexDirection: "row",
          alignItems: "center",
          paddingVertical: appearance === "glass" ? 14 : 10,
          gap: showLeadingIcon ? 10 : 0,
        }}
      >
        {showLeadingIcon ? (
          <View style={{ flexShrink: 0 }}>
            <Calendar size={13} color={colors.textLight} strokeWidth={1.5} />
          </View>
        ) : null}
        <FieldInput
          value={display}
          accessibilityLabel={accessibilityLabel}
          autoFocus={autoFocus}
          editable={!disabled}
          dimWhenDisabled={false}
          onChangeText={(input) => {
            const pastedIso = /^\d{4}-\d{2}-\d{2}$/.test(input)
              ? parseTypedToIso(input, "YYYY-MM-DD")
              : "";
            const formatted = pastedIso
              ? isoToDisplay(pastedIso, dateFormat)
              : formatDateTyping(input, dateFormat, display);
            setDisplay(formatted);
            const iso = parseTypedToIso(formatted, dateFormat);
            onValidityChange?.(Boolean(iso));
            // Only propagate a complete ISO date, or an explicit clear.
            // Emitting the intermediate "" on every keystroke lets callers
            // like PersonalInfo PATCH `dateOfBirth: null` mid-typing, which
            // wipes the previously-saved DOB before the user finishes.
            if (iso) {
              onChange(iso);
            } else if (formatted === "") {
              onChange("");
            }
            if (iso || formatted === "") onIncompleteChange?.(false);
          }}
          onBlur={() => {
            if (display && !parseTypedToIso(display, dateFormat)) {
              onIncompleteChange?.(true);
            }
          }}
          placeholder={placeholder ?? placeholderFor(dateFormat)}
          inputMode="numeric"
          maxLength={10}
          autoComplete="off"
          autoCorrect={false}
          spellCheck={false}
          textRole={appearance === "glass" ? "large" : "adaptive"}
          tone={appearance === "glass" ? "onDark" : "default"}
          layoutStyle={{
            flex: 1,
            minWidth: 0,
            textAlign: appearance === "glass" ? "center" : "left",
          }}
        />
      </View>
    </MoonFieldInput>
  );
}
